/**
 * React hook for Gender Party WebSocket connection
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { config } from "@/config";

// =============================================================================
// Error Messages
// =============================================================================

const ERROR_MESSAGES: Record<string, string> = {
  GENDER_PARTY_NOT_FOUND: "Голосование не найдено",
  STATE_ERROR: "Не удалось загрузить данные. Попробуйте обновить страницу.",
  INVALID_MESSAGE: "Ошибка связи с сервером",
  INTERNAL_ERROR: "Произошла ошибка. Попробуйте позже.",
};

function getReadableErrorMessage(code: string, fallbackMessage: string): string {
  return ERROR_MESSAGES[code] || fallbackMessage || "Произошла ошибка";
}

// =============================================================================
// Types
// =============================================================================

export type GenderPartyStatus =
  | "DRAFT"
  | "VOTING_OPEN"
  | "VOTING_CLOSED"
  | "REVEALED"
  | "COMPLETED";

export type GenderVote = "BOY" | "GIRL";

export type ShowResultsMode = "always" | "after_vote" | "on_reveal";

export interface GenderPartyState {
  id: string;
  name: string;
  description: string | null;
  status: GenderPartyStatus;
  isTest: boolean;
  eventId: string | null;
  eventTitle: string | null;
  partnerOneName: string;
  partnerTwoName: string;
  couplePhotoUrl: string | null;
  allowVoteChange: boolean;
  showResultsMode: ShowResultsMode;
  answer: GenderVote | null;
}

export interface GenderPartyPhoto {
  id: string;
  imageUrl: string;
  caption: string | null;
  sortOrder: number;
}

export interface GenderPartyVoteCounts {
  boyVotes: number;
  girlVotes: number;
  totalVotes: number;
  boyPercentage: number;
  girlPercentage: number;
}

// Client -> Server Events
interface ClientJoinGenderPartyEvent {
  type: "join_gender_party";
  genderPartyId: string;
  accountId?: string;
}

interface ClientLeaveGenderPartyEvent {
  type: "leave_gender_party";
  genderPartyId: string;
}

interface ClientPingEvent {
  type: "ping";
}

type ClientEvent =
  | ClientJoinGenderPartyEvent
  | ClientLeaveGenderPartyEvent
  | ClientPingEvent;

// Server -> Client Events
interface ServerPongEvent {
  type: "pong";
  timestamp: number;
}

interface ServerErrorEvent {
  type: "error";
  code: string;
  message: string;
}

interface ServerGenderPartyJoinedEvent {
  type: "gender_party:joined";
  genderPartyId: string;
}

interface ServerGenderPartyLeftEvent {
  type: "gender_party:left";
  genderPartyId: string;
}

interface ServerGenderPartyStateEvent {
  type: "gender_party:state";
  genderParty: GenderPartyState;
  photos: GenderPartyPhoto[];
  voteCounts: GenderPartyVoteCounts | null;
  myVote: GenderVote | null;
}

interface ServerGenderPartyVoteUpdateEvent {
  type: "gender_party:vote_update";
  genderPartyId: string;
  voteCounts: GenderPartyVoteCounts;
}

interface ServerGenderPartyStatusChangeEvent {
  type: "gender_party:status_change";
  genderPartyId: string;
  status: GenderPartyStatus;
  voteCounts: GenderPartyVoteCounts | null;
}

interface ServerGenderPartyRevealEvent {
  type: "gender_party:reveal";
  genderPartyId: string;
  voteCounts: GenderPartyVoteCounts;
}

interface ServerGenderPartyAnswerEvent {
  type: "gender_party:answer";
  genderPartyId: string;
  answer: GenderVote;
  voteCounts: GenderPartyVoteCounts;
}

interface ServerGenderPartyTestResetEvent {
  type: "gender_party:test_reset";
  genderPartyId: string;
}

type ServerEvent =
  | ServerPongEvent
  | ServerErrorEvent
  | ServerGenderPartyJoinedEvent
  | ServerGenderPartyLeftEvent
  | ServerGenderPartyStateEvent
  | ServerGenderPartyVoteUpdateEvent
  | ServerGenderPartyStatusChangeEvent
  | ServerGenderPartyRevealEvent
  | ServerGenderPartyAnswerEvent
  | ServerGenderPartyTestResetEvent;

// =============================================================================
// WebSocket Client
// =============================================================================

interface WebSocketClientOptions {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
  onMessage?: (event: ServerEvent) => void;
  reconnectDelay?: number;
  maxReconnectAttempts?: number;
}

class GenderPartyWebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private options: WebSocketClientOptions;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isClosedIntentionally = false;
  private currentGenderPartyId: string | null = null;
  private accountId: string | null = null;

  constructor(options: WebSocketClientOptions = {}) {
    const apiUrl = config.apiUrl;
    const wsProtocol = apiUrl.startsWith("https") ? "wss" : "ws";
    const baseUrl = apiUrl.replace(/^https?/, wsProtocol);
    this.url = `${baseUrl}/ws/raffle`; // Same WS endpoint, different events

    this.options = {
      reconnectDelay: 500,
      maxReconnectAttempts: 15,
      ...options,
    };
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isClosedIntentionally = false;

    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log("[GenderParty WS] Connected");
        this.reconnectAttempts = 0;
        this.options.onConnect?.();

        if (this.currentGenderPartyId) {
          this.joinGenderParty(
            this.currentGenderPartyId,
            this.accountId ?? undefined
          );
        }
      };

      this.ws.onclose = () => {
        console.log("[GenderParty WS] Disconnected");
        this.options.onDisconnect?.();

        if (!this.isClosedIntentionally) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error("[GenderParty WS] Error:", error);
        this.options.onError?.(new Error("Проблема с подключением. Проверьте интернет-соединение."));
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as ServerEvent;
          this.options.onMessage?.(data);
        } catch (error) {
          console.error("[GenderParty WS] Failed to parse message:", error);
        }
      };
    } catch (error) {
      console.error("[GenderParty WS] Failed to connect:", error);
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    this.isClosedIntentionally = true;
    this.currentGenderPartyId = null;
    this.accountId = null;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= (this.options.maxReconnectAttempts ?? 15)) {
      console.log("[GenderParty WS] Max reconnect attempts reached");
      this.options.onError?.(new Error("Не удалось подключиться. Проверьте интернет-соединение и попробуйте обновить страницу."));
      return;
    }

    this.reconnectAttempts++;
    const baseDelay = this.options.reconnectDelay ?? 500;
    const delay = Math.min(
      baseDelay * Math.pow(1.5, this.reconnectAttempts - 1),
      5000
    );

    console.log(
      `[GenderParty WS] Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts})`
    );

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private send(event: ClientEvent): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(event));
    }
  }

  joinGenderParty(genderPartyId: string, accountId?: string): void {
    this.currentGenderPartyId = genderPartyId;
    this.accountId = accountId ?? null;

    this.send({
      type: "join_gender_party",
      genderPartyId,
      accountId,
    });
  }

  leaveGenderParty(genderPartyId: string): void {
    if (this.currentGenderPartyId === genderPartyId) {
      this.currentGenderPartyId = null;
      this.accountId = null;
    }

    this.send({
      type: "leave_gender_party",
      genderPartyId,
    });
  }

  ping(): void {
    this.send({ type: "ping" });
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

// =============================================================================
// Hook Types
// =============================================================================

export interface GenderPartySocketState {
  isConnected: boolean;
  isJoined: boolean;
  error: string | null;

  genderParty: GenderPartyState | null;
  photos: GenderPartyPhoto[];
  voteCounts: GenderPartyVoteCounts | null;
  myVote: GenderVote | null;

  isRevealed: boolean;
  isCompleted: boolean;
  revealedAnswer: GenderVote | null;
}

export interface UseGenderPartySocketOptions {
  genderPartyId: string;
  accountId?: string;
  onVoteUpdate?: (voteCounts: GenderPartyVoteCounts) => void;
  onStatusChange?: (status: GenderPartyStatus) => void;
  onReveal?: (voteCounts: GenderPartyVoteCounts) => void;
  onAnswer?: (answer: GenderVote, voteCounts: GenderPartyVoteCounts) => void;
  onTestReset?: () => void;
}

export interface UseGenderPartySocketReturn extends GenderPartySocketState {
  connect: () => void;
  disconnect: () => void;
}

// =============================================================================
// Hook
// =============================================================================

export function useGenderPartySocket(
  options: UseGenderPartySocketOptions
): UseGenderPartySocketReturn {
  const {
    genderPartyId,
    accountId,
    onVoteUpdate,
    onStatusChange,
    onReveal,
    onAnswer,
    onTestReset,
  } = options;

  const [state, setState] = useState<GenderPartySocketState>({
    isConnected: false,
    isJoined: false,
    error: null,
    genderParty: null,
    photos: [],
    voteCounts: null,
    myVote: null,
    isRevealed: false,
    isCompleted: false,
    revealedAnswer: null,
  });

  const clientRef = useRef<GenderPartyWebSocketClient | null>(null);
  const callbacksRef = useRef({
    onVoteUpdate,
    onStatusChange,
    onReveal,
    onAnswer,
    onTestReset,
  });

  useEffect(() => {
    callbacksRef.current = {
      onVoteUpdate,
      onStatusChange,
      onReveal,
      onAnswer,
      onTestReset,
    };
  }, [onVoteUpdate, onStatusChange, onReveal, onAnswer, onTestReset]);

  const handleMessage = useCallback((event: ServerEvent) => {
    switch (event.type) {
      case "gender_party:joined":
        setState((s) => ({ ...s, isJoined: true, error: null }));
        break;

      case "gender_party:left":
        setState((s) => ({ ...s, isJoined: false }));
        break;

      case "error":
        setState((s) => ({
          ...s,
          error: getReadableErrorMessage(event.code, event.message),
        }));
        break;

      case "gender_party:state":
        setState((s) => ({
          ...s,
          genderParty: event.genderParty,
          photos: event.photos,
          voteCounts: event.voteCounts,
          myVote: event.myVote,
          isRevealed:
            event.genderParty.status === "REVEALED" ||
            event.genderParty.status === "COMPLETED",
          isCompleted: event.genderParty.status === "COMPLETED",
          revealedAnswer:
            event.genderParty.status === "COMPLETED"
              ? event.genderParty.answer
              : null,
        }));
        break;

      case "gender_party:vote_update":
        setState((s) => ({
          ...s,
          voteCounts: event.voteCounts,
        }));
        callbacksRef.current.onVoteUpdate?.(event.voteCounts);
        break;

      case "gender_party:status_change":
        setState((s) => ({
          ...s,
          genderParty: s.genderParty
            ? { ...s.genderParty, status: event.status }
            : null,
          voteCounts: event.voteCounts ?? s.voteCounts,
          isRevealed:
            event.status === "REVEALED" || event.status === "COMPLETED",
        }));
        callbacksRef.current.onStatusChange?.(event.status);
        break;

      case "gender_party:reveal":
        setState((s) => ({
          ...s,
          genderParty: s.genderParty
            ? { ...s.genderParty, status: "REVEALED" }
            : null,
          voteCounts: event.voteCounts,
          isRevealed: true,
        }));
        callbacksRef.current.onReveal?.(event.voteCounts);
        break;

      case "gender_party:answer":
        setState((s) => ({
          ...s,
          genderParty: s.genderParty
            ? { ...s.genderParty, status: "COMPLETED", answer: event.answer }
            : null,
          voteCounts: event.voteCounts,
          isCompleted: true,
          revealedAnswer: event.answer,
        }));
        callbacksRef.current.onAnswer?.(event.answer, event.voteCounts);
        break;

      case "gender_party:test_reset":
        setState((s) => ({
          ...s,
          genderParty: s.genderParty
            ? { ...s.genderParty, status: "DRAFT", answer: null }
            : null,
          voteCounts: null,
          myVote: null,
          isRevealed: false,
          isCompleted: false,
          revealedAnswer: null,
        }));
        callbacksRef.current.onTestReset?.();
        break;
    }
  }, []);

  const connect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.disconnect();
    }

    const client = new GenderPartyWebSocketClient({
      onConnect: () => {
        setState((s) => ({ ...s, isConnected: true, error: null }));
        client.joinGenderParty(genderPartyId, accountId);
      },
      onDisconnect: () => {
        setState((s) => ({ ...s, isConnected: false, isJoined: false }));
      },
      onError: (error) => {
        setState((s) => ({ ...s, error: error.message }));
      },
      onMessage: handleMessage,
    });

    clientRef.current = client;
    client.connect();
  }, [genderPartyId, accountId, handleMessage]);

  const disconnect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.disconnect();
      clientRef.current = null;
    }
    setState((s) => ({
      ...s,
      isConnected: false,
      isJoined: false,
    }));
  }, []);

  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  useEffect(() => {
    if (clientRef.current?.isConnected) {
      clientRef.current.joinGenderParty(genderPartyId, accountId);
    }
  }, [genderPartyId, accountId]);

  return {
    ...state,
    connect,
    disconnect,
  };
}
